import { z } from "zod";

export const preferencesValidationKeyPrefix = "views.preferences.validation.";

export const firstNameRequiredErrorKey = "firstNameRequired";
export const firstNameTooShortErrorKey = "firstNameTooShort";
export const firstNameTooLongErrorKey = "firstNameTooLong";
export const firstNameInvalidErrorKey = "firstNameInvalid";
export const lastNameRequiredErrorKey = "lastNameRequired";
export const lastNameTooShortErrorKey = "lastNameTooShort";
export const lastNameTooLongErrorKey = "lastNameTooLong";
export const lastNameInvalidErrorKey = "lastNameInvalid";
export const emailRequiredErrorKey = "emailRequired";
export const invalidEmailErrorKey = "invalidEmail";
export const streetRequiredErrorKey = "streetRequired";
export const streetTooShortErrorKey = "streetTooShort";
export const streetTooLongErrorKey = "streetTooLong";
export const cityRequiredErrorKey = "cityRequired";
export const cityTooShortErrorKey = "cityTooShort";
export const cityTooLongErrorKey = "cityTooLong";
export const stateRequiredErrorKey = "stateRequired";
export const zipRequiredErrorKey = "zipRequired";
export const zipTooShortErrorKey = "zipTooShort";
export const zipInvalidErrorKey = "zipInvalid";
export const countryRequiredErrorKey = "countryRequired";
export const ADDRESS_MAX_LENGTH = 60;
export const ADDRESS_SPLIT_LENGTH = 30;
export const MIN_NAME_LENGTH = 2;
export const MAX_NAME_LENGTH = 30;
export const MIN_PHONE_LENGTH = 10;
export const MAX_PHONE_LENGTH = 25;
export const MIN_STREET_LENGTH = 3;
export const MIN_CITY_LENGTH = 2;
export const MAX_CITY_LENGTH = 30;
export const MIN_ZIP_LENGTH = 5;
export const MAX_ZIP_LENGTH = 15;
const NAME_PATTERN = /^[\p{L}\p{M}'\-\s.]+$/u;

export const UserAddressSchema = z.object({
  city: z.string().optional(),
  country: z.string().optional(),
  countryCode: z.string().optional(),
  geocodeAccuracy: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  postalCode: z.string().optional(),
  state: z.string().optional(),
  stateCode: z.string().optional(),
  street: z.string().optional(),
});

export const AddressSchema = z.object({
  name: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  address1: z.string().nullable().optional(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  provinceCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
});

export const UserProfileSchema = z.object({
  userId: z.string(),
  name: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().optional(),
  email: z.string(),
  languageLocaleKey: z.string(),
  vendorLogoUrl: z.string().optional(),
  billingAddress: UserAddressSchema.optional(),
  shippingAddress: AddressSchema.optional(),
});

export const UserProfileResponseSchema = z.object({
  data: UserProfileSchema,
  timestamp: z.string(),
  path: z.string(),
});

export const UserProfileFetchResponseSchema = z.object({
  data: UserProfileSchema,
  timestamp: z.string(),
  path: z.string(),
});

export const UserProfileRequestSchema = UserProfileSchema.omit({ billingAddress: true, name: true, vendorLogoUrl: true }).extend({
  shippingAddress: AddressSchema.omit({ latitude: true, longitude: true, province: true, firstName: true, lastName: true, country: true }),
});

export const createUserProfilePreferencesFormSchema = (requireProvince: boolean) =>
  UserProfileRequestSchema.extend({
    name: z.string().optional(),
    vendorLogoUrl: z.string().optional(),
    firstName: z
      .string()
      .trim()
      .min(1, { message: firstNameRequiredErrorKey })
      .min(MIN_NAME_LENGTH, { message: firstNameTooShortErrorKey })
      .max(MAX_NAME_LENGTH, { message: firstNameTooLongErrorKey })
      .regex(NAME_PATTERN, { message: firstNameInvalidErrorKey }),
    lastName: z
      .string()
      .trim()
      .min(1, { message: lastNameRequiredErrorKey })
      .min(MIN_NAME_LENGTH, { message: lastNameTooShortErrorKey })
      .max(MAX_NAME_LENGTH, { message: lastNameTooLongErrorKey })
      .regex(NAME_PATTERN, { message: lastNameInvalidErrorKey }),
    phone: z.string().trim().optional(),
    email: z.email({ message: invalidEmailErrorKey }).trim().min(1, { message: emailRequiredErrorKey }),
    shippingAddress: AddressSchema.extend({
      address1: z
        .string()
        .trim()
        .min(1, { message: streetRequiredErrorKey })
        .min(MIN_STREET_LENGTH, { message: streetTooShortErrorKey })
        .max(ADDRESS_MAX_LENGTH, { message: streetTooLongErrorKey }),
      city: z
        .string()
        .trim()
        .min(1, { message: cityRequiredErrorKey })
        .min(MIN_CITY_LENGTH, { message: cityTooShortErrorKey })
        .max(MAX_CITY_LENGTH, { message: cityTooLongErrorKey }),
      provinceCode: requireProvince ? z.string().trim().min(1, { message: stateRequiredErrorKey }) : z.string().trim().optional(),
      zip: z
        .string()
        .trim()
        .min(1, { message: zipRequiredErrorKey })
        .min(MIN_ZIP_LENGTH, { message: zipTooShortErrorKey })
        .max(MAX_ZIP_LENGTH, { message: zipInvalidErrorKey }),
      countryCode: z.string().trim().min(1, { message: countryRequiredErrorKey }),
    }),
  });

export const UserProfilePreferencesFormSchema = createUserProfilePreferencesFormSchema(true);

const StateSchema = z.object({
  StateCode: z.string(),
  Name: z.string(),
});

const CountrySchema = z.object({
  Name: z.string(),
  CountryCode: z.string(),
  States: z.array(StateSchema),
});

export const UserProfileCountryStateSchema = z.object({
  stateField: z.literal("StateCode"),
  objectName: z.literal("User"),
  countryField: z.literal("CountryCode"),
  countries: z.array(CountrySchema),
});

export type UserAddress = z.infer<typeof UserAddressSchema>;
export type Address = z.infer<typeof AddressSchema>;
export type UserProfile = z.infer<typeof UserProfileSchema>;
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>;
export type UserProfileFetchResponse = z.infer<typeof UserProfileFetchResponseSchema>;
export type UserProfileRequest = z.infer<typeof UserProfileRequestSchema>;
export type UserProfilePreferencesForm = z.infer<typeof UserProfilePreferencesFormSchema>;
export type UserProfileCountryResponse = z.infer<typeof UserProfileCountryStateSchema>;
export type UserProfileState = z.infer<typeof StateSchema>;
export type UserProfileCountry = z.infer<typeof CountrySchema>;
